
import React, { createContext, useContext, useState, useCallback } from "react";

type PopupType = "coupon" | "notification" | "confirmation" | "custom";

interface PopupData {
  id: string;
  type: PopupType;
  title?: string;
  message?: string;
  component?: React.ReactNode;
  onClose?: () => void;
  persistent?: boolean;
}

interface PopupContextType {
  popups: PopupData[];
  showPopup: (popup: Omit<PopupData, "id">) => string;
  hidePopup: (id: string) => void;
  hideAllPopups: () => void;
}

const PopupContext = createContext<PopupContextType | undefined>(undefined);

export const PopupProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [popups, setPopups] = useState<PopupData[]>([]);

  const showPopup = useCallback((popup: Omit<PopupData, "id">) => {
    const id = `popup-${Date.now()}`;
    setPopups((prev) => [...prev, { ...popup, id }]);
    return id;
  }, []);

  const hidePopup = useCallback((id: string) => {
    setPopups((prev) => prev.filter((popup) => popup.id !== id));
  }, []);

  const hideAllPopups = useCallback(() => {
    setPopups([]);
  }, []);

  return (
    <PopupContext.Provider value={{ popups, showPopup, hidePopup, hideAllPopups }}>
      {children}
    </PopupContext.Provider>
  );
};

export const usePopup = () => {
  const context = useContext(PopupContext);
  if (context === undefined) {
    throw new Error("usePopup must be used within a PopupProvider");
  }
  return context;
};
